import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { getTourDetail, type TourDetailData } from "@/api/tour";
import { resolveDetailId } from "@/utils/detailNav";
import { toastWarning } from "@/utils/toast";
import { preCreateGroupTourOrder } from "./api/order";

import BasicInfo from "./components/BasicInfo";
import RouteInfo from "./components/RouteInfo";
import BookingSection, { type BookingFormData } from "./components/BookingSection";
import Itinerary from "@/components/Itinerary";
import ScrollToBottom from "@/components/ScrollToBottom";

const BRAND = "#FF6B35";

export default function RouteDetailProductPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // ★ 同时支持 URL query（?routeId=x）与 location.state（统一走工具函数，读取顺序一致）
  const routeId = resolveDetailId("routeId", searchParams, location.state);

  const [detail, setDetail] = useState<TourDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);

  // 出行信息表单
  const [bookingData, setBookingData] = useState<BookingFormData>({
    departureDate: "",
    endDate: "",
    priceId: 0,
    roomType: "",
    salePrice: "",
    singleRoomSupplement: "",
    singleBikeSeat: "",
    singleRoomCount: 0,
    singleBikeCount: 0,
    peopleCount: 1,
    contactName: "",
    contactPhone: "",
    remark: "",
  });

  // 加载线路详情
  useEffect(() => {
    if (!routeId) { setLoading(false); return; }
    getTourDetail(routeId)
      .then((res) => {
        if (res.data.code === 200) setDetail(res.data.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [routeId]);

  // ── 预订相关计算 ──
  const baseAmount = bookingData.salePrice ? Number(bookingData.salePrice) * bookingData.peopleCount : 0;
  const singleRoomAmount = bookingData.singleRoomCount > 0 && bookingData.singleRoomSupplement
    ? Number(bookingData.singleRoomSupplement) * bookingData.singleRoomCount
    : 0;
  const singleBikeAmount = bookingData.singleBikeCount > 0 && bookingData.singleBikeSeat
    ? Number(bookingData.singleBikeSeat) * bookingData.singleBikeCount
    : 0;
  const total = baseAmount + singleRoomAmount + singleBikeAmount;

  // 是否已填写必要信息（日期段 + 档次 + 联系人）
  const isReady = !!(
    bookingData.priceId > 0 &&
    bookingData.departureDate &&
    bookingData.contactName.trim() &&
    bookingData.contactPhone.trim()
  );

  // 立即预订
  const handleBook = async () => {
    if (!isReady || !detail) return;
    if (!bookingData.departureDate) { toastWarning("请选择出发日期"); return; }
    if (!bookingData.contactName.trim()) { toastWarning("请填写联系人姓名"); return; }
    if (!bookingData.contactPhone.trim()) { toastWarning("请填写联系电话"); return; }

    setBookingLoading(true);
    try {
      // 预创建订单
      const res = await preCreateGroupTourOrder({
        group_tour_id: detail.id,
      });

      if (res.data.code !== 200) {
        toastWarning(res.data.message || "预创建订单失败");
        return;
      }

      navigate("/order-form-tour", {
        state: {
          type: "tour",
          routeId: detail.id,
          routeTitle: detail.title,
          unitPrice: bookingData.salePrice ? Number(bookingData.salePrice) : 0,
          totalAmount: total,
          preCreatedData: res.data.data,
          bookingFormData: bookingData,
        },
      });
    } catch {
      toastWarning("网络错误，请稍后重试");
    } finally {
      setBookingLoading(false);
    }
  };

  // Loading 状态
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F9FC" }}>
        <span className="text-sm text-gray-400">加载中...</span>
      </div>
    );
  }

  // 数据为空
  if (!detail) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F9FC", fontFamily: "'Inter','SF Pro Text',sans-serif" }}>
        <div className="text-center">
          <i className="ri-error-warning-line" style={{ fontSize: 40, color: "#9CA3AF" }} />
          <p className="text-sm text-gray-500 mt-3">线路不存在或已下架</p>
          <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 rounded-full text-xs font-semibold cursor-pointer" style={{ background: "#1E3A8A", color: "#fff" }}>
            返回
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+144px)]" style={{ background: "#F8F9FC", fontFamily: "'Inter','SF Pro Text',sans-serif" }}>
      <BasicInfo detail={detail} onBack={() => navigate(-1)} />
      <RouteInfo detail={detail} />
      <Itinerary list={detail.itinerary ?? []} />

      {/* 填写出行信息（能走到这里说明 detail 与 routeId 均有效，用 detail.id 保证类型安全） */}
      {!loading && detail && (
        <BookingSection
          routeId={detail.id}
          onChange={setBookingData}
        />
      )}

      <BottomBar
        ready={isReady}
        total={total}
        loading={bookingLoading}
        onSubmit={handleBook}
      />

      {/* 吸底按钮（点击滚动到页面最底部，始终固定显示） */}
      <ScrollToBottom bottomOffset={200} />
    </div>
  );
}

interface BottomBarProps {
  ready: boolean;
  total: number;
  loading?: boolean;
  onSubmit: () => void | Promise<void>;
}

function BottomBar({ ready, total, loading, onSubmit }: BottomBarProps) {
  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-white z-40"
      style={{ borderTop: "1px solid #F5F5F5", paddingTop: "16px", paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}
    >
      <div className="px-4 pt-4">
        <button
          onClick={onSubmit}
          disabled={!ready || loading}
          className="w-full py-3.5 rounded-2xl text-sm font-bold cursor-pointer active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: ready
              ? `linear-gradient(135deg, ${BRAND}, #FF9A5C)`
              : "#E5E7EB",
            color: ready ? "#fff" : "#9CA3AF",
          }}
        >
          {loading ? "处理中..." : (ready ? `立即预订 ¥${total.toLocaleString()}` : "请先选择出团时间和档次")}
        </button>
      </div>
    </div>
  );
}
